import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import RichTextEditor from '../RichTextEditor';
import Avatar from '../Avatar';
import { orgLabel } from '../../utils/orgTaxonomy';
import WithPresence from '../PresenceDot';
import { useRecentPicks } from '../../utils/recentPicks';
import { RecentPickLabel, RecentPickDivider } from '../RecentPickLabel';
import { GROUP_DESCRIPTION_LIMIT } from './chatLimits';

/**
 * Create a custom group (F3.1).
 *
 * Any employee may do this — that is what F3.4's "Diwali Committee" means: a
 * group that needs no manager's involvement to exist. Attaching it to a project
 * is optional and only affects where its files are filed and what the group is
 * listed under; the auto-created project group is a separate thing this cannot
 * make a second copy of.
 */
const NewGroupModal = ({ onClose, onCreated }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [projectId, setProjectId] = useState('');
    const [projects, setProjects] = useState([]);
    const [people, setPeople] = useState([]);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    const descriptionTooLong = description.length > GROUP_DESCRIPTION_LIMIT;

    useEffect(() => {
        api.get('/projects').then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
    }, []);

    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const res = await api.get('/conversations/contacts', { params: { q: query } });
                if (!cancelled) setPeople(res.data || []);
            } catch {
                if (!cancelled) setPeople([]);
            }
        }, query ? 250 : 0);
        return () => { cancelled = true; clearTimeout(t); };
    }, [query]);

    const searchRef = useRef(null);
    const recentPeople = useRecentPicks('chat-people');
    const recentProjects = useRecentPicks('project');
    const toggle = (id) => {
        if (!selected.includes(id)) recentPeople.record(id);
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
        // Ready for the next name: the search empties and keeps the cursor.
        setQuery('');
        searchRef.current?.focus();
    };

    const create = async () => {
        if (!name.trim()) {
            Swal.fire('Name it', 'A group needs a name.', 'info');
            return;
        }
        if (descriptionTooLong) {
            Swal.fire('Shorten the description', `Use ${GROUP_DESCRIPTION_LIMIT.toLocaleString()} characters or fewer, including formatting.`, 'info');
            return;
        }
        setSaving(true);
        try {
            const res = await api.post('/conversations/group', {
                name: name.trim(),
                description: description.trim(),
                projectId: projectId || null,
                memberIds: selected
            });
            onCreated(res.data);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not create the group.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // With nothing typed, the people picked lately come first.
    const { recent: recentRows, rest: otherRows } = query.trim()
        ? { recent: [], rest: people }
        : recentPeople.split(people);

    const renderPerson = (p) => (
        <div
            key={p._id}
            className={`msgr-pick ${selected.includes(p._id) ? 'selected' : ''}`}
            onClick={() => toggle(p._id)}
        >
            <WithPresence userId={p._id} small>
                <Avatar name={p.name} profilePic={p.profilePic} className="msgr-avatar" />
            </WithPresence>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{p.name}</div>
                <div style={{ fontSize: 11.5, color: 'light-dark(#667781, var(--theme-muted))' }}>
                    {[orgLabel(p.jobTitle) || p.role, orgLabel(p.department)].filter(Boolean).join(' · ')}
                </div>
            </div>
            {selected.includes(p._id) && (
                <FontAwesomeIcon icon={faCheck} style={{ color: 'light-dark(#128c7e, var(--theme-success-ink, #7bcf9f))' }} />
            )}
        </div>
    );

    return (
        <div className="msgr-modal-back" onClick={onClose}>
            <div className="msgr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="msgr-modal-head">
                    <h4>New group</h4>
                    <button className="msgr-icon-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="msgr-modal-body">
                    <div className="msgr-field">
                        <label>Group name</label>
                        <input
                            autoFocus
                            placeholder="e.g. Design — Spectra"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={80}
                        />
                    </div>

                    <div className="msgr-field">
                        <label>Description (optional)</label>
                        <RichTextEditor
                            rows={2}
                            placeholder="What is this group for?"
                            value={description}
                            onChange={setDescription}
                            maxLength={GROUP_DESCRIPTION_LIMIT}
                            enforceMaxLength={false}
                            ariaLabel="Group description"
                        />
                        {descriptionTooLong && <p className="msgr-description-error" role="alert">Description exceeds {GROUP_DESCRIPTION_LIMIT.toLocaleString()} characters, including formatting. Shorten it to create the group.</p>}
                    </div>

                    <div className="msgr-field">
                        <label>Link to a project (optional)</label>
                        <select value={projectId} onChange={(e) => { setProjectId(e.target.value); recentProjects.record(e.target.value); }}>
                            <option value="">No project — a standalone group</option>
                            {recentProjects.order(projects).map((p) => (
                                <option key={p._id} value={p._id}>{p.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="msgr-field">
                        <label>Members {selected.length > 0 && `(${selected.length} selected)`}</label>
                        <div className="msgr-search" style={{ marginBottom: 8 }}>
                            <FontAwesomeIcon icon={faMagnifyingGlass} className="msgr-search-icon" />
                            <input
                                ref={searchRef}
                                placeholder="Search people"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                style={{ border: '1px solid light-dark(#e4e6eb, var(--theme-border, #33363b))' }}
                            />
                        </div>

                        <div className="msgr-picklist">
                            {!people.length && <div className="msgr-empty">Nobody matches that.</div>}
                            {recentRows.length > 0 && <RecentPickLabel />}
                            {recentRows.map(renderPerson)}
                            {recentRows.length > 0 && otherRows.length > 0 && <RecentPickDivider />}
                            {otherRows.map(renderPerson)}
                        </div>
                    </div>
                </div>

                <div className="msgr-modal-foot">
                    <button className="msgr-btn" onClick={onClose}>Cancel</button>
                    <button className="msgr-btn primary" onClick={create} disabled={saving || !name.trim() || descriptionTooLong}>
                        {saving ? 'Creating…' : 'Create group'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewGroupModal;
